import React from 'react';
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import spinner from '../../../images/spinner-ripple.svg';
import AngledAxisTick from '../AngledAxisTick';
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';
import OutputFieldsTooltipSection from './OutputFieldsTooltipSection';
import ChartDataTable from '../../graphData/ChartDataTable';
import { seriesTable } from '../../../helpers/dataHelpers/chartDataTable';

/**
 * DashboardBarchartSmall
 *
 * Accepts the same data structure as OutputMultiLineGraph:
 *  data = [
 *    { name: '2025-10-01', 'Output A': { total: 10, projects: {...} }, 'Output B': { total: 5, projects: {...} } },
 *    ...
 *  ]
 *
 * If `outputs` is provided: renders a bar per output using row[output.Title]?.total.
 * If `outputs` is omitted: falls back to a flat numeric key (valueKey, default 'Resultaat').
 *
 * Props:
 * - data, isLoading
 * - outputs?: [{ ID, Title }]
 * - color?: string | { [outputId]: string }
 * - graphColor?: string (axes/ticks color)
 * - height?: number (default 400)
 * - width?: number|string (default '100%')
 * - displayLegend?: boolean (default true if outputs provided)
 * - valueKey?: string (fallback key when no outputs; default 'Resultaat')
 * - fieldSummaries?: OutputFieldSummaries — the activity's other fields per date,
 *   shown under the totals in the tooltip
 */

/**
 * Tooltip with the output totals of one date, plus the activity's other fields.
 * Only used when `fieldSummaries` is passed; otherwise recharts' default stays.
 *
 * @param {object} props
 * @param {boolean} [props.active] - Set by recharts
 * @param {Array<{name: string, value: number, color: string}>} [props.payload] - Set by recharts
 * @param {string} [props.label] - The date, set by recharts
 * @param {import('./OutputFieldsTooltipSection').OutputFieldSummaries} props.fieldSummaries
 * @returns {JSX.Element|null}
 */
const OutputBarTooltip = ({ active, payload, label, fieldSummaries }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="output-bar-tooltip">
      <p className="output-bar-tooltip-label">{label}</p>
      {payload.map(entry => (
        <p key={entry.name} className="output-bar-tooltip-total">{entry.name}: {entry.value}</p>
      ))}
      <OutputFieldsTooltipSection fieldSummaries={fieldSummaries} label={label} />
    </div>
  );
};
const DashboardBarchartSmall = ({
  data = [],
  isLoading = false,
  outputs,
  graphColor,
  barColor,
  height = 400,
  width = '100%',
  displayLegend,
  valueKey = 'Resultaat',
  fieldSummaries,
}) => {
  const hasOutputs = Array.isArray(outputs) && outputs.length > 0;
  const showLegend = displayLegend !== false && hasOutputs;

  return (
    <>
      {isLoading ? (
        <div id="graph-loading-container">
          <img src={spinner} alt="spinner" className="spinner" />
        </div>
      ) : (
        <ResponsiveContainer width={width} height={height}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis
              dataKey="name"
              stroke={graphColor || 'black'}
              tick={<AngledAxisTick fill={graphColor || 'black'} />}
              height={70}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke={graphColor || 'black'}
              tick={{ fill: graphColor || 'black' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={fieldSummaries ? <OutputBarTooltip fieldSummaries={fieldSummaries} /> : undefined} />
            {showLegend && <Legend wrapperStyle={graphColor ? { color: graphColor } : undefined} />}

            {hasOutputs ? (
              // Same access pattern as OutputMultiLineGraph (row[output.Title]?.total)
              outputs.map((output) => (
                <Bar
                  key={output.ID || output.Title}
                  name={output.Title}
                  dataKey={(row) =>
                    row?.[output.Title]?.total ? row[output.Title].total : 0
                  }
                  fill={barColor}
                  stroke={CHART_MARK_STROKE}
                  strokeWidth={CHART_MARK_STROKE_WIDTH}
                  activeBar={
                    <Rectangle
                      fill={barColor}
                      stroke={barColor}
                    />
                  }
                />
              ))
            ) : (
              // Fallback: flat numeric data (old behavior), e.g. { name, Resultaat }
              <Bar
                dataKey={valueKey}
                fill={barColor}
                stroke={CHART_MARK_STROKE}
                strokeWidth={CHART_MARK_STROKE_WIDTH}
                activeBar={
                  <Rectangle
                    fill={barColor}
                    stroke={barColor}
                  />
                }
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
      {/* De cijfers onder de grafiek, voor de PDF en de kopie. */}
      {!isLoading && hasOutputs && (
        <ChartDataTable table={seriesTable(data, outputs.map(output => ({ key: output.Title, label: output.Title })))} />
      )}
    </>
  );
};

export default DashboardBarchartSmall;
